import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getUsers, type KeycloakUser, type UserFilters, type UsersResponse } from '@/api/users';
import type { DataTableResponse } from '@/components/data-table';
import { CreateUserModal } from '@/components/modals/create-user-modal';
import { SearchInput } from '@/components/search-input';
import { UserDetailsDialog } from '@/components/user-details-dialog';
import { UsersTable } from '@/components/users-table';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useFocusReturn } from '@/hooks/use-focus-return';

const mapSortByToBackend = (frontendKey: string): string => {
  const mapping: Record<string, string> = {
    username: 'username',
    email: 'email',
    firstName: 'firstName',
    lastName: 'lastName',
    createdTimestamp: 'createdTimestamp',
  };
  return mapping[frontendKey] || 'createdTimestamp';
};

export const Users: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useKeycloakAuth();
  const [usersData, setUsersData] = useState<UsersResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdTimestamp',
    order: 'desc',
  });
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<KeycloakUser | null>(null);
  const [isUserDetailsOpen, setIsUserDetailsOpen] = useState(false);
  const { capture: captureFocus, restore: restoreFocus } = useFocusReturn();

  const fetchUsers = useCallback(async (currentFilters: UserFilters) => {
    const isInitialLoad = !hasLoadedRef.current;
    try {
      if (isInitialLoad) {
        setIsLoading(true);
      }
      setError(null);
      const data = await getUsers(currentFilters);
      setUsersData(data);
      hasLoadedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  }, []);

  const handleSortChange = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    setFilters((prev) => ({
      ...prev,
      sortBy: mapSortByToBackend(sortBy),
      order,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleUserCreated = useCallback(() => {
    toast.success('User created successfully');
    fetchUsers(filters);
  }, [fetchUsers, filters]);

  const handleUserClick = useCallback(
    (user: KeycloakUser) => {
      captureFocus();
      setSelectedUser(user);
      setIsUserDetailsOpen(true);
    },
    [captureFocus]
  );

  const handleUserDetailsClose = useCallback(() => {
    restoreFocus();
    setIsUserDetailsOpen(false);
    setSelectedUser(null);
  }, [restoreFocus]);

  const handleUserUpdated = useCallback(() => {
    toast.success('User updated successfully');
    fetchUsers(filters);
  }, [fetchUsers, filters]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchUsers(filters);
    } else if (!authLoading && !isAuthenticated) {
      setError('Please log in to view users');
      setIsLoading(false);
    }
  }, [filters, fetchUsers, isAuthenticated, authLoading]);

  // Debounce search
  useEffect(() => {
    const debounce = setTimeout(() => {
      setFilters((prev) => {
        const nextSearch = search.trim();
        if ((prev.search ?? '') === nextSearch) return prev;
        const { search: _omit, ...rest } = prev;
        return nextSearch ? { ...rest, page: 1, search: nextSearch } : { ...rest, page: 1 };
      });
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const tableData: DataTableResponse<KeycloakUser> | undefined = usersData
    ? {
        data: usersData.data,
        pagination: {
          page: usersData.pagination.page,
          pageSize: usersData.pagination.limit,
          total: usersData.pagination.total,
          totalPages: usersData.pagination.totalPages,
        },
      }
    : undefined;

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Users</h1>
          </div>
          <CreateUserModal onSuccess={handleUserCreated} />
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <UsersTable
          data={tableData}
          isLoading={isLoading}
          error={error}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onUserClick={handleUserClick}
          searchInput={
            <SearchInput
              id="users-search"
              value={search}
              onChange={setSearch}
              placeholder="Search users..."
            />
          }
        />
      </div>

      <UserDetailsDialog
        open={isUserDetailsOpen}
        onClose={handleUserDetailsClose}
        user={selectedUser}
        onUserUpdated={handleUserUpdated}
      />
    </div>
  );
};
